import { useState, useEffect } from "react";
import COLORS from "./config/colors";

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/ryanvukich/30min";

export default function BookingConfirmed() {
  const [bookingData, setBookingData] = useState(null);
  const [trade, setTrade] = useState("");
  const [market, setMarket] = useState("");

  useEffect(() => {
    // Read URL params
    const params = new URLSearchParams(window.location.search);
    setTrade(params.get("trade") || "");
    setMarket(params.get("market") || "");

    // Read booking data stored in sessionStorage before Stripe redirect
    try {
      const stored = sessionStorage.getItem("ultralife_booking");
      if (stored) {
        setBookingData(JSON.parse(stored));
        sessionStorage.removeItem("ultralife_booking");
      }
    } catch {
      // sessionStorage unavailable or parse error — show generic success
    }
  }, []);

  const tradeDisplay =
    bookingData?.trade ||
    (trade ? trade.charAt(0).toUpperCase() + trade.slice(1) : "Project");
  const marketDisplay = bookingData?.market || market || "";
  const depositDisplay =
    bookingData?.depositAmount != null
      ? `$${Math.round(bookingData.depositAmount).toLocaleString()}`
      : null;
  const totalDisplay =
    bookingData?.totalEstimate != null
      ? `$${Math.round(bookingData.totalEstimate).toLocaleString()}`
      : null;

  const nextSteps = [
    "We'll call you within 24 hours to confirm your project details and schedule.",
    "We'll schedule a free site visit to verify measurements and finalize scope.",
    "You'll receive a formal contract with your fixed-bid price — no overruns, ever.",
    "We'll schedule your start date and get to work.",
  ];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: COLORS.cream,
        fontFamily: "'DM Sans', sans-serif",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700;800&display=swap"
        rel="stylesheet"
      />

      <div
        style={{
          maxWidth: 560,
          width: "100%",
          background: COLORS.white,
          borderRadius: 20,
          padding: 48,
          boxShadow: "0 4px 24px rgba(26,35,50,0.08)",
          border: "1px solid rgba(155,142,130,0.12)",
        }}
      >
        {/* Success icon */}
        <div
          style={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "rgba(74,124,89,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
        >
          <svg
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke={COLORS.pine}
            strokeWidth="2.5"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        {/* Headline */}
        <div
          style={{
            fontSize: 28,
            fontWeight: 700,
            fontFamily: "'Playfair Display', serif",
            color: COLORS.bark,
            textAlign: "center",
            marginBottom: 12,
          }}
        >
          Your Project is Booked!
        </div>

        {/* Deposit confirmation */}
        <div
          style={{
            fontSize: 16,
            color: COLORS.stone,
            textAlign: "center",
            marginBottom: 28,
            lineHeight: 1.6,
          }}
        >
          {depositDisplay ? (
            <>
              We've received your 16.5% deposit of{" "}
              <strong style={{ color: COLORS.honey }}>{depositDisplay}</strong>.
              <br />
              Thank you for choosing UltraLife Trades!
            </>
          ) : (
            "We've received your deposit. Thank you for choosing UltraLife Trades!"
          )}
        </div>

        {/* Project summary */}
        {(bookingData || tradeDisplay || marketDisplay || totalDisplay) && (
          <div
            style={{
              background: COLORS.snow,
              borderRadius: 12,
              padding: "16px 20px",
              marginBottom: 24,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                color: COLORS.stone,
                marginBottom: 12,
              }}
            >
              Project Summary
            </div>
            {bookingData?.customerName && (
              <Row label="Customer" value={bookingData.customerName} />
            )}
            {tradeDisplay && <Row label="Trade" value={tradeDisplay} />}
            {marketDisplay && <Row label="Market" value={marketDisplay} />}
            {totalDisplay && (
              <Row label="Total Estimate" value={totalDisplay} />
            )}
            {depositDisplay && (
              <Row
                label="Deposit Paid"
                value={depositDisplay}
                highlight
              />
            )}
          </div>
        )}

        {/* What happens next */}
        <div
          style={{
            background: "rgba(196,145,59,0.06)",
            border: "1px solid rgba(196,145,59,0.18)",
            borderRadius: 12,
            padding: "16px 20px",
            marginBottom: 32,
          }}
        >
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: COLORS.bark,
              marginBottom: 12,
            }}
          >
            What Happens Next
          </div>
          {nextSteps.map((step, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                marginBottom: i < nextSteps.length - 1 ? 10 : 0,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  background: COLORS.honey,
                  color: COLORS.white,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  flexShrink: 0,
                  marginTop: 1,
                }}
              >
                {i + 1}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: COLORS.bark,
                  lineHeight: 1.55,
                }}
              >
                {step}
              </div>
            </div>
          ))}
        </div>

        {/* Schedule site visit — Calendly */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <a
            href={`${CALENDLY_URL}?name=${encodeURIComponent(bookingData?.customerName || "")}&email=${encodeURIComponent(bookingData?.customerEmail || "")}&back=1`}
            style={{
              display: "inline-block",
              padding: "16px 36px",
              background: COLORS.honey,
              color: COLORS.white,
              borderRadius: 10,
              textDecoration: "none",
              fontWeight: 700,
              fontSize: 15,
              fontFamily: "'DM Sans', sans-serif",
              letterSpacing: 0.3,
              boxShadow: "0 4px 16px rgba(196,145,59,0.35)",
            }}
          >
            Schedule Your Free Site Visit
          </a>
          <div style={{ fontSize: 12, color: COLORS.stone, marginTop: 8 }}>
            We'll confirm your project details and finalize scope
          </div>
        </div>

        {/* Return button */}
        <div style={{ textAlign: "center" }}>
          <a
            href="/"
            style={{
              display: "inline-block",
              padding: "12px 36px",
              background: "transparent",
              color: COLORS.bark,
              borderRadius: 10,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 13,
              fontFamily: "'DM Sans', sans-serif",
              letterSpacing: 0.3,
              border: `1px solid rgba(155,142,130,0.3)`,
            }}
          >
            Return to UltraLife Trades
          </a>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, highlight = false }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        marginBottom: 8,
        fontSize: 13,
      }}
    >
      <span style={{ color: COLORS.stone }}>{label}</span>
      <span
        style={{
          fontWeight: 600,
          color: highlight ? COLORS.pine : COLORS.bark,
        }}
      >
        {value}
      </span>
    </div>
  );
}
